import type { ComponentType, ReactNode } from "react";

/**
 * Generic building blocks for the named-config registry.
 *
 * The pattern (one file per component family, e.g. menus.ts):
 *   1. describe items as pure data — never JSX, never callbacks;
 *   2. export a named `as const`-style map so consumers import by unique name;
 *   3. handlers live at the usage site, keyed by the item's `id`.
 *
 * Adding a new named config = ~5 lines in the family file. See menus.ts.
 */

/** One selectable entry in a menu. `A` is the literal union of action ids. */
export interface MenuItemConfig<A extends string = string> {
  /** Action key — the contract between config and the consumer's onAction. */
  id: A;
  label: string;
  /** Optional second line rendered under the label (e.g. Data Studio Transform items). */
  description?: string;
  /** Explanation shown in a tooltip on hover or focus; keeps the row to one line (use instead of `description` for a note). */
  hint?: string;
  /** Component reference (not JSX) so config stays serializable-ish pure data.
   *  `size` is string | number to stay assignable from lucide-react icons. */
  icon?: ComponentType<{ size?: number | string }>;
  danger?: boolean;
  disabled?: boolean;
  /** Not built yet: shown disabled with a Soon badge and the "Coming soon" tooltip. */
  soon?: boolean;
  /** Display string for a keyboard shortcut, shown right-aligned (display only; the menu binds no keys). */
  shortcut?: string;
  /** Replaces the icon slot (e.g. a colour swatch). */
  leading?: ReactNode;
  /** Small secondary text at the end of the row, e.g. a count or a status ("On"). Opt-in; `hint` is the tooltip. */
  endHint?: string;
  /** Shown at the end of the row (e.g. a check mark). */
  trailing?: ReactNode;
  /** Marks the row as the current choice (aria-current). */
  current?: boolean;
  /** Render a divider line above this item. */
  dividerBefore?: boolean;
  /** A small group label above this item (the "panel" ActionMenu variant). */
  sectionLabel?: string;
}

export type MenuConfig<A extends string = string> = ReadonlyArray<
  MenuItemConfig<A>
>;

/**
 * Identity helper that preserves the literal `id` union
 * (TS 4.9-compatible — no `const` type parameters).
 */
export const defineMenu = <A extends string>(
  items: ReadonlyArray<MenuItemConfig<A>>,
): ReadonlyArray<MenuItemConfig<A>> => items;

/** Union of action ids for a menu config: MenuActionId<typeof menus.aiSummaryEdit>. */
export type MenuActionId<T extends MenuConfig> = T[number]["id"];
